"use client";
import React, { useState } from "react";
import { motion } from "motion/react";
import { Send, CheckCircle2, XCircle, Loader2 } from "lucide-react";

const fieldClass =
  "w-full rounded-2xl border border-white/10 bg-[#10151c] px-4 py-3.5 text-[#f3efe6] placeholder:text-[#9aa6b5] outline-none transition focus:border-[#3ee0c0]/60 focus:ring-2 focus:ring-[#3ee0c0]/30";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: "success",
          message: data.message || "Message sent successfully!",
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
      } else {
        setSubmitStatus({
          type: "error",
          message: data.message || "Failed to send message. Please try again.",
        });
      }
    } catch {
      setSubmitStatus({
        type: "error",
        message: "Network error. Please try again later.",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setSubmitStatus({ type: null, message: "" });
      }, 5000);
    }
  };

  return (
    <motion.section
      id="contact"
      className="scroll-mt-28 py-20 sm:py-28"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#3ee0c0]">
          Contact
        </p>
        <motion.h2
          initial={{ y: 24, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="font-display mt-3 text-4xl sm:text-5xl"
        >
          Let&apos;s build the next one.
        </motion.h2>
        <motion.p
          initial={{ y: 24, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="mt-4 max-w-2xl text-[#9aa6b5]"
        >
          Have a product, a role, or a collaboration in mind? Send a note and
          I&apos;ll get back to you.
        </motion.p>
        <motion.form
          initial={{ y: 24, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-10 grid grid-cols-1 gap-4"
          onSubmit={handleSubmit}
        >
          {submitStatus.type && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex items-center gap-3 rounded-2xl border p-4 ${
                submitStatus.type === "success"
                  ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
                  : "border-red-500/30 bg-red-500/10 text-red-200"
              }`}
            >
              {submitStatus.type === "success" ? (
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
              ) : (
                <XCircle className="h-5 w-5 flex-shrink-0" />
              )}
              <span>{submitStatus.message}</span>
            </motion.div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input
              required
              placeholder="Your name"
              className={fieldClass}
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isSubmitting}
            />
            <input
              type="email"
              required
              placeholder="Your email"
              className={fieldClass}
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
            />
          </div>
          <input
            placeholder="Subject"
            className={fieldClass}
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <textarea
            name="message"
            required
            rows={6}
            placeholder="Your message"
            className={`${fieldClass} resize-none`}
            value={formData.message}
            onChange={handleChange}
            disabled={isSubmitting}
          />
          <motion.button
            whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
            whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
            type="submit"
            disabled={isSubmitting}
            className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-full bg-[#3ee0c0] px-6 py-3.5 font-medium text-[#06110e] transition hover:bg-[#6aead0] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send Message
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
    </motion.section>
  );
};

export default ContactForm;
